/**
 * MADFERIT conversion features.
 *
 * - <mf-free-shipping>: free-shipping progress meter (converts the shop-currency threshold
 *   into the shopper's presentment currency when they browse in another currency).
 * - <mf-money>: renders a shop-currency amount in the shopper's currency.
 * - <mf-upsell>: "complete the look" quick add inside the cart drawer.
 * - <mf-modal>: opens/closes a native <dialog> from a trigger button (size guide).
 * - <mf-welcome-popup>: email sign-up offer with frequency capping and code auto-apply.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/** @returns {{ active: string | undefined, rate: number }} */
function presentmentCurrency() {
  const currency = /** @type {any} */ (window).Shopify?.currency;
  const rate = Number(currency?.rate);
  return {
    active: currency?.active,
    rate: Number.isFinite(rate) && rate > 0 ? rate : 1,
  };
}

/**
 * Formats a major-unit amount in the given currency, using the page language.
 * @param {number} amount
 * @param {string} currency
 */
function formatMajor(amount, currency) {
  const locale = document.documentElement.lang || 'es';
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    });
    const formatted = formatter.format(amount);
    // "$" is ambiguous across currencies; make non-MXN dollars explicit.
    return formatted.includes('$') && currency !== 'MXN' ? `${formatted} ${currency}` : formatted;
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Converts a shop-currency amount (in cents) into the presentment currency (major units).
 * @param {number} shopCents
 * @param {string} shopCurrency
 * @param {string} targetCurrency
 */
function convertShopCents(shopCents, shopCurrency, targetCurrency) {
  const major = shopCents / 100;
  if (!targetCurrency || targetCurrency === shopCurrency) return major;
  return major * presentmentCurrency().rate;
}

/* ------------------------------------------------------------------------------------------ */

class MfFreeShipping extends HTMLElement {
  static get observedAttributes() {
    return ['data-total-cents', 'data-currency'];
  }

  connectedCallback() {
    this.update();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.update();
  }

  update() {
    const shopCurrency = this.dataset.shopCurrency || 'MXN';
    const currency = this.dataset.currency || shopCurrency;
    // Same-currency carts are fully rendered by Liquid; only convert when needed.
    if (currency === shopCurrency) return;

    const totalMajor = Number(this.dataset.totalCents || 0) / 100;
    const thresholdMajor = convertShopCents(Number(this.dataset.thresholdShopCents || 0), shopCurrency, currency);
    if (!(thresholdMajor > 0)) return;

    const remaining = Math.max(0, thresholdMajor - totalMajor);
    const progress = Math.min(100, Math.round((totalMajor / thresholdMajor) * 100));
    const unlocked = remaining <= 0.009;

    const message = this.querySelector('[data-mf-message]');
    const track = this.querySelector('[role="progressbar"]');
    const fill = this.querySelector('[data-mf-fill]');

    if (message) {
      const template = unlocked ? this.dataset.templateUnlocked : this.dataset.templateRemaining;
      if (template) {
        message.innerHTML = template.replace('[amount]', formatMajor(Math.ceil(remaining * 100) / 100, currency));
      }
    }
    if (track) track.setAttribute('aria-valuenow', String(progress));
    if (fill instanceof HTMLElement) fill.style.setProperty('--mf-progress', String(progress / 100));
    this.classList.toggle('is-unlocked', unlocked);
  }
}

class MfMoney extends HTMLElement {
  connectedCallback() {
    const shopCurrency = this.dataset.shopCurrency || 'MXN';
    const currency = this.dataset.currency || presentmentCurrency().active || shopCurrency;
    if (currency === shopCurrency) return;
    const amount = convertShopCents(Number(this.dataset.shopCents || 0), shopCurrency, currency);
    if (amount > 0) this.textContent = formatMajor(Math.ceil(amount), currency);
  }
}

/* ------------------------------------------------------------------------------------------ */

class MfUpsell extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('change', this.#onChange, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const form = select.closest('form');
    form?.querySelector('[data-mf-hint]')?.setAttribute('hidden', '');
    form?.classList.remove('is-invalid');
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-mf-upsell-form]')) return;
    event.preventDefault();

    const select = form.querySelector('select[name="id"]');
    const button = form.querySelector('button[type="submit"]');
    const hint = form.querySelector('[data-mf-hint]');
    if (!(select instanceof HTMLSelectElement) || !(button instanceof HTMLButtonElement)) return;

    if (!select.value) {
      form.classList.add('is-invalid');
      hint?.removeAttribute('hidden');
      select.focus();
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    form.classList.add('is-loading');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', select.value);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: select.value, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((res) => res.json());

      const ajaxCart = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((res) =>
        res.json()
      );

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'mf-upsell', itemCount: 1 },
        });
        if (hint) {
          hint.textContent = response.description || response.message || '';
          hint.removeAttribute('hidden');
        }
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'mf-upsell',
          itemCount: 1,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      if (form.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        form.classList.remove('is-loading');
      }
    }
  };
}

/* ------------------------------------------------------------------------------------------ */

class MfModal extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const trigger = this.querySelector('[data-mf-modal-open]');
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    trigger?.addEventListener('click', () => dialog.showModal(), { signal });
    dialog.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target === dialog || (target instanceof Element && target.closest('[data-mf-modal-close]'))) {
          dialog.close();
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

/* ------------------------------------------------------------------------------------------ */

const POPUP_STORAGE_KEY = 'mf-welcome-popup';

/** @returns {{ state?: string, at?: number }} */
function readPopupState() {
  try {
    return JSON.parse(window.localStorage.getItem(POPUP_STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/** @param {string} state */
function writePopupState(state) {
  try {
    window.localStorage.setItem(POPUP_STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
  } catch {
    /* storage unavailable: the popup simply may show again */
  }
}

class MfWelcomePopup extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {number | undefined} */
  #timer;
  #opened = false;

  get dialog() {
    return this.querySelector('dialog');
  }

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const dialog = this.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target === dialog || (target instanceof Element && target.closest('[data-mf-popup-close]'))) {
          this.close();
        }
      },
      { signal }
    );
    dialog.addEventListener('cancel', () => this.#markDismissed(), { signal });
    this.querySelector('form')?.addEventListener('submit', () => writePopupState('submitted'), { signal });
    this.querySelector('[data-mf-copy]')?.addEventListener('click', this.#copyCode, { signal });

    const isOwnForm = window.location.hash === `#${this.dataset.formId}`;

    if (this.dataset.success === 'true') {
      // Any newsletter sign-up on this page counts, so the offer is never shown again.
      writePopupState('subscribed');
      // Signed up through the popup: show the code right away and apply it to the cart.
      if (isOwnForm) {
        this.open();
        this.#applyCode();
      }
      return;
    }

    // The sign-up failed (for example an invalid email): reopen so the error is visible.
    if (this.dataset.error === 'true' && isOwnForm) {
      this.open();
      return;
    }

    // Theme editor: preview the popup while its section is selected.
    if (this.dataset.designMode === 'true') {
      document.addEventListener(
        'shopify:section:select',
        (event) => {
          if (/** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId) this.open();
        },
        { signal }
      );
      document.addEventListener(
        'shopify:section:deselect',
        (event) => {
          if (/** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId) this.dialog?.close();
        },
        { signal }
      );
      return;
    }

    const saved = readPopupState();
    const days = Number(this.dataset.snoozeDays || 7);
    if (saved.state === 'subscribed') return;
    if (saved.state && saved.at && Date.now() - saved.at < days * 86400000) return;

    const delay = Math.max(3, Number(this.dataset.delay || 10)) * 1000;
    this.#timer = window.setTimeout(() => this.#tryOpen(), delay);

    if (window.matchMedia('(pointer: fine)').matches) {
      document.addEventListener(
        'mouseout',
        (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.#tryOpen();
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearTimeout(this.#timer);
  }

  #tryOpen = () => {
    if (this.#opened) return;
    // Never interrupt the cart drawer, quick add or any other open dialog.
    if (document.querySelector('dialog[open]')) {
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(this.#tryOpen, 4000);
      return;
    }
    this.open();
  };

  open() {
    const dialog = this.dialog;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    this.#opened = true;
    window.clearTimeout(this.#timer);
    dialog.showModal();
    // Avoid popping the keyboard on touch screens: focus the panel, not the input.
    const panel = dialog.querySelector('[data-mf-popup-panel]');
    if (panel instanceof HTMLElement) panel.focus({ preventScroll: true });
  }

  close() {
    const dialog = this.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;
    this.#markDismissed();
    dialog.close();
  }

  #markDismissed() {
    if (this.dataset.designMode === 'true') return;
    if (readPopupState().state !== 'subscribed') writePopupState('dismissed');
  }

  #copyCode = async () => {
    const code = this.dataset.code || '';
    const button = this.querySelector('[data-mf-copy]');
    try {
      await navigator.clipboard.writeText(code);
      if (button instanceof HTMLElement) {
        const original = button.textContent;
        button.textContent = button.dataset.copiedLabel || '✓';
        window.setTimeout(() => {
          button.textContent = original;
        }, 2000);
      }
    } catch {
      /* Clipboard blocked: the code stays visible to copy manually. */
    }
  };

  async #applyCode() {
    const code = (this.dataset.code || '').trim();
    if (!code) return;
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((res) => res.json());
      const existing = (cart.discount_codes || []).map((/** @type {{ code: string }} */ entry) => entry.code);
      if (existing.some((/** @type {string} */ value) => value.toLowerCase() === code.toLowerCase())) return;
      const config = fetchConfig('json', { body: JSON.stringify({ discount: [...existing, code].join(',') }) });
      await fetch(Theme.routes.cart_update_url + '.js', config);
      this.querySelector('[data-mf-applied]')?.removeAttribute('hidden');
    } catch {
      /* The code is still shown so the shopper can enter it at checkout. */
    }
  }
}

const definitions = /** @type {Array<[string, CustomElementConstructor]>} */ ([
  ['mf-free-shipping', MfFreeShipping],
  ['mf-money', MfMoney],
  ['mf-upsell', MfUpsell],
  ['mf-modal', MfModal],
  ['mf-welcome-popup', MfWelcomePopup],
]);

for (const [name, constructor] of definitions) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
